import proj4 from 'proj4';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box3, Matrix4Tuple, Vector3 } from 'three';

import { GeodataModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import { isDefined } from '../../../../utils/variables';
import { Wgs84Coordinates } from '../../../../viewer/utilities/math-helpers';
import { GeodataImagesContext } from '../geodata-images-state';
import { GeodataContext } from '../geodata-state';
import { getPositionFromGeodataImageTransform } from './geodata-camera-utils';

const ORIGO_MOVED_THRESHOLD = 10_000;

export interface GeodataSpatialReferenceContext {
  origo?: Vector3;
  boundingBox?: Box3;
  projection?: string;
  getImageLocalPosition?: (transform: Matrix4Tuple) => Vector3;
}

export const GeodataSpatialReferenceContext = React.createContext<GeodataSpatialReferenceContext>(
  undefined!,
);

type Props = {
  children: React.ReactNode;
};

function useGcpBoundingBoxAndOrigo(geodata: GeodataModel) {
  const [gcpOrigo, setGcpOrigo] = useState<Vector3 | undefined>(undefined);
  const [gcpBoundingBox, setGcpBoundingBox] = useState<Box3 | undefined>(undefined);

  useEffect(() => {
    if (!geodata.gcpCollection?.points || geodata.gcpCollection?.points.length === 0) {
      setGcpBoundingBox(undefined);
      setGcpOrigo(undefined);
      return;
    }

    const points = geodata.gcpCollection?.points.map((point) => new Vector3(...point.enu));
    const bb = new Box3().setFromPoints(points);
    setGcpBoundingBox(bb);
    setGcpOrigo(bb.getCenter(new Vector3()));
  }, [geodata.gcpCollection?.points]);

  return { gcpOrigo, gcpBoundingBox };
}

function useImagesWgs84Postions() {
  const [imageWgs84Postions, setImageWgs84Postions] = useState<Wgs84Coordinates[] | undefined>(
    undefined,
  );
  const { images } = useContext(GeodataImagesContext);

  useEffect(() => {
    if (images.length === 0) {
      setImageWgs84Postions(undefined);
      return;
    }

    const imagePoints: Wgs84Coordinates[] = [];
    for (const image of images) {
      if (!isDefined(image.transform) || image.enabled === false || image.aligned === false) {
        continue;
      }

      imagePoints.push(getPositionFromGeodataImageTransform(image.transform));
    }

    setImageWgs84Postions(imagePoints);
  }, [images]);

  return { imageWgs84Postions };
}

function useImagesBoundingBoxAndOrigo(
  imageWgs84Postions?: Wgs84Coordinates[],
  wgs84ToProjectCoords?: proj4.Converter,
) {
  const [imagesBoundingBox, setImagesBoundingBox] = useState<Box3 | undefined>(undefined);
  const [imagesOrigo, setImagesOrigo] = useState<Vector3 | undefined>(undefined);

  useEffect(() => {
    if (!wgs84ToProjectCoords || !imageWgs84Postions) {
      setImagesBoundingBox(undefined);
      setImagesOrigo(undefined);
      return;
    }

    const coordinates = imageWgs84Postions.map((position) => {
      const projectCoords = wgs84ToProjectCoords.forward([
        position.longitude,
        position.latitude,
        position.height,
      ]);
      return new Vector3(projectCoords[0], projectCoords[1], projectCoords[2]);
    });

    const bb = new Box3().setFromPoints(coordinates);
    setImagesBoundingBox(bb);
    setImagesOrigo(bb.getCenter(new Vector3()));
  }, [imageWgs84Postions, wgs84ToProjectCoords]);

  return { imagesOrigo, imagesBoundingBox };
}

export const GeodataSpatialReferenceState = (props: Props) => {
  const { geodata } = useContext(GeodataContext);

  const proj4Projection = useMemo(() => {
    return geodata.coordinateSystem.proj4;
  }, [geodata]);

  const { gcpOrigo, gcpBoundingBox } = useGcpBoundingBoxAndOrigo(geodata);
  const { imageWgs84Postions } = useImagesWgs84Postions();

  const wgs84ToProjectCoords = useMemo(() => {
    if (!proj4Projection) {
      return undefined;
    }

    return proj4('wgs84', proj4Projection);
  }, [proj4Projection]);

  const { imagesOrigo, imagesBoundingBox } = useImagesBoundingBoxAndOrigo(
    imageWgs84Postions,
    wgs84ToProjectCoords,
  );

  const desiredOrigo = useMemo(() => {
    if (imagesOrigo) {
      return imagesOrigo;
    }

    if (gcpOrigo) {
      return gcpOrigo;
    }

    return undefined;
  }, [gcpOrigo, imagesOrigo]);

  const [origo, setOrigo] = useState<Vector3 | undefined>(undefined);

  // Only update origo if it moved by more than the threshold.
  useEffect(() => {
    if (!desiredOrigo) {
      return;
    }

    if (!isDefined(origo)) {
      setOrigo(desiredOrigo);
      return;
    }

    if (desiredOrigo.distanceTo(origo) > ORIGO_MOVED_THRESHOLD) {
      setOrigo(desiredOrigo);
    }
  }, [desiredOrigo, origo]);

  const boundingBox = useMemo(() => {
    // Don't set bounding box until origo has stabilized.
    if (!origo) {
      return undefined;
    }

    const bbox = imagesBoundingBox ?? gcpBoundingBox;
    if (bbox) {
      return bbox.clone().translate(origo.clone().negate());
    }

    return undefined;
  }, [gcpBoundingBox, imagesBoundingBox, origo]);

  const getImageLocalPosition = useMemo(() => {
    if (!origo || !wgs84ToProjectCoords) {
      return undefined;
    }

    return (transform: Matrix4Tuple): Vector3 => {
      const wgs84Position = getPositionFromGeodataImageTransform(transform);
      const projectCoords = wgs84ToProjectCoords.forward([
        wgs84Position.longitude,
        wgs84Position.latitude,
        wgs84Position.height,
      ]);
      return new Vector3(projectCoords[0], projectCoords[1], projectCoords[2]).sub(origo);
    };
  }, [origo, wgs84ToProjectCoords]);

  const contextValue = useMemo<GeodataSpatialReferenceContext>(() => {
    return {
      origo,
      boundingBox,
      projection: proj4Projection,
      getImageLocalPosition,
    };
  }, [origo, boundingBox, proj4Projection, getImageLocalPosition]);

  return (
    <GeodataSpatialReferenceContext.Provider value={contextValue}>
      {props.children}
    </GeodataSpatialReferenceContext.Provider>
  );
};
